import React, { useState, useMemo, useEffect, useRef } from 'react';
import { XMarkIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { Combobox } from '@headlessui/react';
import AirItemTypeCategoryGroupDropdown from '../../../sharedComponents/AirItemTypeCategoryGroupDropdown';
import AirItemTypeService from '../../setting/airItemType/AirItemTypeService';
import BrandService from '../../setting/brand/BrandService';
import { toast } from 'react-toastify';
import { formatAmountRound } from '../../../utils/formatAmount';

const BRAND_TIER_LABEL = { normal: 'Normal', street_brand: 'Street', brand_name: 'Brand Name' };
const TIER_PRIORITY = { normal: 1, street_brand: 2, brand_name: 3 };

// Get the highest tier: brand_name > street_brand > normal
function getHigherTier(tier1, tier2) {
  const p1 = TIER_PRIORITY[tier1] || 0;
  const p2 = TIER_PRIORITY[tier2] || 0;
  if (p1 >= p2) return tier1;
  return tier2;
}

// Determine which tier the price implies based on types' price ranges
function getPriceTier(price, itemsInGroup) {
  if (price == null || isNaN(price)) return null;
  // Check each tier from highest to lowest
  for (const tier of ['brand_name', 'street_brand', 'normal']) {
    const typeWithTier = itemsInGroup.find((t) => t.brandTier === tier);
    if (!typeWithTier) continue;
    const min = typeWithTier.priceRangeMin != null ? typeWithTier.priceRangeMin : -Infinity;
    const max = typeWithTier.priceRangeMax != null ? typeWithTier.priceRangeMax : Infinity;
    if (price >= min && price <= max) return tier;
  }
  return null;
}

function groupByCategoryAndType(list) {
  const byCat = {};
  (list || []).forEach((item) => {
    if (item.isActive === false) return; // skip only if explicitly inactive
    const cat = item.category || 'other';
    const typ = item.type || 'other';
    if (!byCat[cat]) byCat[cat] = {};
    if (!byCat[cat][typ]) byCat[cat][typ] = [];
    byCat[cat][typ].push(item);
  });
  return byCat;
}

const AirItemPriceModal = ({ onClose, airItemTypesList = [] }) => {
  const [url, setUrl] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedType, setSelectedType] = useState(null);

  const [brandQuery, setBrandQuery] = useState('');
  const [brandSearchResults, setBrandSearchResults] = useState([]);
  const [brandSearching, setBrandSearching] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [itemPriceJpy, setItemPriceJpy] = useState('');
  const searchDebounceRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const grouped = useMemo(() => groupByCategoryAndType(airItemTypesList), [airItemTypesList]);
  const categories = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const groupsInCategory = useMemo(() => {
    if (!selectedCategory) return [];
    const types = grouped[selectedCategory];
    return types ? Object.keys(types).sort() : [];
  }, [grouped, selectedCategory]);

  const itemsInGroup = useMemo(() => {
    if (!selectedCategory || !selectedGroup) return [];
    const types = grouped[selectedCategory];
    if (!types || !types[selectedGroup]) return [];
    const list = types[selectedGroup] || [];
    // Order buttons left-to-right: lowest price to highest (by amountPerUnit)
    return [...list].sort((a, b) => {
      const pa = parseFloat(a.amountPerUnit);
      const pb = parseFloat(b.amountPerUnit);
      if (Number.isNaN(pa) && Number.isNaN(pb)) return 0;
      if (Number.isNaN(pa)) return 1;
      if (Number.isNaN(pb)) return -1;
      return pa - pb;
    });
  }, [grouped, selectedCategory, selectedGroup]);

  const groupHasBrandTier = useMemo(
    () => itemsInGroup.some((t) => t.brandTier || t.priceRangeMin != null || t.priceRangeMax != null),
    [itemsInGroup]
  );

  const brandTier = selectedBrand ? selectedBrand.brandType : null;
  const priceNum = itemPriceJpy === '' || itemPriceJpy == null ? null : parseInt(itemPriceJpy, 10);

  // Calculate "effective tier" = max(brand tier, price-implied tier)
  // Priority: brand_name > street_brand > normal
  const effectiveTier = useMemo(() => {
    if (!groupHasBrandTier) return null;
    const priceTier = getPriceTier(priceNum, itemsInGroup);
    if (!brandTier && !priceTier) return null;
    if (!brandTier) return priceTier;
    if (!priceTier) return brandTier;
    return getHigherTier(brandTier, priceTier);
  }, [groupHasBrandTier, brandTier, priceNum, itemsInGroup]);

  // When group changes: clear type (category+group are set together by double dropdown)
  useEffect(() => {
    setSelectedType(null);
  }, [selectedGroup]);

  // Single item in group → auto-select (only when selection is empty for this group)
  useEffect(() => {
    if (itemsInGroup.length !== 1) return;
    const only = itemsInGroup[0];
    const onlyId = String(only.id ?? only.value);
    if (!selectedType) {
      setSelectedType(only);
      return;
    }
    const currentId = String(selectedType.id ?? selectedType.value);
    if (currentId !== onlyId) setSelectedType(only);
  }, [itemsInGroup, selectedType]);

  useEffect(() => {
    if (!brandQuery.trim()) {
      setBrandSearchResults([]);
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setBrandSearching(true);
      try {
        const res = await BrandService.getBrands({ search: brandQuery.trim(), isActive: true });
        setBrandSearchResults(res.data?.data || []);
      } catch {
        setBrandSearchResults([]);
      } finally {
        setBrandSearching(false);
      }
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [brandQuery]);

  const rateResult = useMemo(() => {
    if (!selectedType) return null;
    const method = selectedType.calculationMethod;
    const normalAmount = parseFloat(selectedType.amountPerUnit);
    const sendOnlyAmount = parseFloat(selectedType.sendOnlyAmountPerUnit) || normalAmount;
    if (isNaN(normalAmount)) return null;
    const unit = method === 'per_kg' ? 'kg' : 'pcs';
    return { normalAmount, sendOnlyAmount, unit };
  }, [selectedType]);

  const handleSaveQuote = async () => {
    if (!url.trim()) {
      toast.warning('กรุณากรอก URL');
      return;
    }
    if (!selectedType) {
      toast.warning('กรุณาเลือกประเภทสินค้า');
      return;
    }
    setSaving(true);
    try {
      const typeId = selectedType.id ?? selectedType.value;
      await AirItemTypeService.createPriceQuote({
        url: url.trim(),
        airItemTypeId: typeId,
        brandId: selectedBrand?.id || undefined,
        itemPriceJpy: itemPriceJpy === '' || itemPriceJpy == null ? undefined : parseInt(itemPriceJpy, 10),
      });
      toast.success('บันทึกราคาที่แจ้งลูกค้า สำเร็จ');
    } catch (err) {
      toast.error('ไม่สามารถบันทึกราคาที่แจ้งลูกค้าได้');
    } finally {
      setSaving(false);
    }
  };

  // Always show brand+price inputs when 2+ buttons
  const showBrandAndPriceInputs = itemsInGroup.length > 1;

  // Auto-select based on effectiveTier (max of brand tier and price tier)
  useEffect(() => {
    if (!effectiveTier || itemsInGroup.length === 0) return;
    // Find the type that matches the effective tier
    const match = itemsInGroup.find((t) => t.brandTier === effectiveTier);
    if (!match) return;
    const matchId = String(match.id ?? match.value);
    const currentId = selectedType ? String(selectedType.id ?? selectedType.value) : null;
    if (currentId !== matchId) setSelectedType(match);
  }, [effectiveTier, itemsInGroup, selectedType]);

  return (
    <div className="h-full flex flex-col p-4 max-h-[85vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-3 shrink-0">
        <h2 className="text-lg font-semibold text-gray-900">Air Item Rate</h2>
        <button type="button" onClick={onClose} className="rounded p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100" aria-label="Close">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="w-full space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL สินค้า</label>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
          />
        </div>

        {/* Step 1 & 2: Category > Group (double dropdown) */}
        <AirItemTypeCategoryGroupDropdown
          grouped={grouped}
          value={{ category: selectedCategory, group: selectedGroup }}
          onChange={(cat, grp) => {
            setSelectedCategory(cat);
            setSelectedGroup(grp);
          }}
          placeholder="เลือกหมวดหมู่ > กลุ่ม"
          label="หมวดหมู่ > กลุ่ม"
          buttonClassName="w-full flex items-center justify-between rounded border border-gray-300 bg-white px-3 py-2 text-left text-sm"
        />

        {/* Step 3: Only when group has multiple typeNames */}
        {selectedCategory && selectedGroup && itemsInGroup.length > 1 && (
          <>
            {/* Show brand + price whenever 2+ buttons, to help admin choose; filter applies when group has brand tier */}
            {showBrandAndPriceInputs && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded space-y-3">
                {!groupHasBrandTier && (
                  <p className="text-xs text-amber-700">ชนิดในกลุ่มนี้ไม่มีระดับยี่ห้อ/ช่วงราคา — เลือกปุ่มด้านล่าง</p>
                )}
                <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <label className="block text-sm font-medium text-amber-800 mb-1">ยี่ห้อ (Brand)</label>
                  <Combobox
                    value={selectedBrand}
                    onChange={(brand) => {
                      setSelectedBrand(brand);
                      setBrandQuery(brand ? brand.name : '');
                    }}
                  >
                    <Combobox.Input
                      className="w-full rounded border border-amber-300 bg-white px-3 py-2 text-sm"
                      displayValue={(b) => (b ? b.name : brandQuery)}
                      onChange={(e) => {
                        setBrandQuery(e.target.value);
                        if (!e.target.value) setSelectedBrand(null);
                      }}
                      placeholder="พิมพ์ชื่อยี่ห้อ"
                    />
                    <Combobox.Button className="absolute right-0 top-7 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                    </Combobox.Button>
                    <Combobox.Options className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded border bg-white py-1 text-sm shadow-lg">
                      {brandSearching && <div className="px-3 py-2 text-gray-500">กำลังค้นหา...</div>}
                      {!brandSearching && brandQuery.trim() && brandSearchResults.length === 0 && (
                        <div className="px-3 py-2 text-gray-500">ไม่พบยี่ห้อ</div>
                      )}
                      {brandSearchResults.map((brand) => (
                        <Combobox.Option key={brand.id} value={brand}>
                          {({ active }) => (
                            <div className={`flex justify-between px-3 py-2 ${active ? 'bg-indigo-50' : ''}`}>
                              <span>{brand.name}</span>
                              <span className="text-gray-500 text-xs">{BRAND_TIER_LABEL[brand.brandType] || brand.brandType}</span>
                              {selectedBrand?.id === brand.id && <CheckIcon className="h-4 w-4 text-indigo-600" />}
                            </div>
                          )}
                        </Combobox.Option>
                      ))}
                    </Combobox.Options>
                  </Combobox>
                  {selectedBrand && (
                    <p className="mt-1 text-xs text-amber-700">
                      ระดับยี่ห้อ: {BRAND_TIER_LABEL[selectedBrand.brandType] || selectedBrand.brandType}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-1">ราคาสินค้า (JPY)</label>
                  <input
                    type="number"
                    value={itemPriceJpy}
                    onChange={(e) => setItemPriceJpy(e.target.value)}
                    placeholder="เช่น 100000"
                    min="0"
                    className="w-full rounded border border-amber-300 bg-white px-3 py-2 text-sm"
                  />
                </div>
                </div>
                {/* Show effective tier calculation when we have both brand and price */}
                {effectiveTier && brandTier && priceNum != null && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                    <strong>คิดราคาตาม:</strong> {BRAND_TIER_LABEL[effectiveTier]}
                    {effectiveTier !== brandTier && (
                      <span className="ml-1">(ราคาสินค้าอยู่ในช่วง {BRAND_TIER_LABEL[effectiveTier]} จึงคิดตามราคา)</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TypeName choice: buttons - always show ALL items, auto-select based on effective tier */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เลือกชนิด ({itemsInGroup.length} รายการ)
                {effectiveTier && (
                  <span className="ml-2 text-xs text-amber-600 font-normal">
                    แนะนำ: {BRAND_TIER_LABEL[effectiveTier] || effectiveTier}
                  </span>
                )}
              </label>
              <div className="flex flex-wrap gap-2">
                {itemsInGroup.map((item) => {
                  const id = item.id ?? item.value;
                  const selectedId = selectedType?.id ?? selectedType?.value;
                  const isSelected = selectedType && String(selectedId) === String(id);
                  const isRecommended = effectiveTier && item.brandTier === effectiveTier;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setSelectedType(item)}
                      className={`px-3 py-2 rounded border text-sm whitespace-nowrap ${
                        isSelected
                          ? 'border-indigo-600 bg-indigo-50 text-indigo-900 font-medium ring-2 ring-indigo-300'
                          : isRecommended
                          ? 'border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {item.typeName || item.text}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Single item in group: show selected info */}
        {selectedCategory && selectedGroup && itemsInGroup.length === 1 && selectedType && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm">
            <p className="text-gray-600">
              <span className="font-medium text-gray-900">{selectedType.typeName || selectedType.text}</span>
            </p>
          </div>
        )}

        {selectedType && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm">
            <p className="text-gray-600">
              <span className="text-gray-400">{selectedType.category}</span>
              {selectedType.category && ' > '}
              <span className="text-gray-500">{selectedType.type}</span>
              {selectedType.type && ' > '}
              <span className="font-medium text-gray-900">{selectedType.typeName || selectedType.text}</span>
            </p>
          </div>
        )}

        {rateResult && (
          <div className="flex gap-3">
            <div className="flex-1 min-w-0 rounded-lg border p-3 bg-green-50 border-green-200">
              <p className="text-xs font-medium text-gray-600 mb-0.5">รายการปกติ</p>
              <p className="text-lg font-semibold text-green-900">
                {formatAmountRound(rateResult.normalAmount)}/{rateResult.unit}
              </p>
            </div>
            <div className="flex-1 min-w-0 rounded-lg border p-3 bg-blue-50 border-blue-200">
              <p className="text-xs font-medium text-gray-600 mb-0.5">รายการฝากส่ง</p>
              <p className="text-lg font-semibold text-blue-900">
                {formatAmountRound(rateResult.sendOnlyAmount)}/{rateResult.unit}
              </p>
            </div>
          </div>
        )}

        <div className="pt-2">
          <button
            type="button"
            onClick={handleSaveQuote}
            disabled={saving || !url.trim() || !selectedType}
            className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'กำลังบันทึก...' : 'บันทึกราคาที่แจ้งลูกค้า'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AirItemPriceModal;
